import { parsePhoneNumber, CountryCode } from 'libphonenumber-js'
import { PhoneValidationResult } from '../types/CountryCode'
import { getCountryByIso } from './countryCodes'

/**
 * Validates a phone number for a given country code
 * @param phone - The phone number to validate (national format, without country code)
 * @param countryCode - ISO 3166-1 alpha-2 country code (e.g., "US", "GB")
 * @returns PhoneValidationResult with isValid flag and optional error message
 */
export function validatePhoneNumber(
  phone: string,
  countryCode: string
): PhoneValidationResult {
  // If phone is empty, let the required attribute handle it
  if (!phone || phone.trim() === '') {
    return { isValid: true }
  }

  // If no country code is selected, prompt user to select one
  if (!countryCode) {
    return {
      isValid: false,
      errorMessage: 'Please select a country code',
    }
  }

  try {
    // Clean the phone number - remove spaces, dashes, parentheses, etc.
    const cleanedPhone = phone.replace(/[\s\-().]/g, '')

    // Parse the phone number with the given country code
    const phoneNumber = parsePhoneNumber(cleanedPhone, countryCode as CountryCode)

    if (!phoneNumber) {
      return {
        isValid: false,
        errorMessage: 'Invalid phone number format',
      }
    }

    // Check if it's a valid phone number for the country
    if (!phoneNumber.isValid()) {
      const country = getCountryByIso(countryCode)
      const countryName = country?.name || countryCode

      // Try to get more specific error
      if (!phoneNumber.isPossible()) {
        return {
          isValid: false,
          errorMessage: `Phone number has incorrect length for ${countryName}`,
        }
      }

      return {
        isValid: false,
        errorMessage: `Phone number is not valid for ${countryName}`,
      }
    }

    return { isValid: true }
  } catch (error) {
    // Handle parsing errors
    const country = getCountryByIso(countryCode)
    const countryName = country?.name || countryCode

    return {
      isValid: false,
      errorMessage: `Invalid phone number for ${countryName}`,
    }
  }
}

/**
 * Format a phone number to E.164 format for storage
 * @param phone - The phone number to format
 * @param countryCode - ISO country code
 * @returns Formatted phone number or original if formatting fails
 */
export function formatPhoneE164(phone: string, countryCode: string): string {
  try {
    const cleanedPhone = phone.replace(/[\s\-().]/g, '')
    const phoneNumber = parsePhoneNumber(cleanedPhone, countryCode as CountryCode)
    if (phoneNumber && phoneNumber.isValid()) {
      return phoneNumber.format('E.164')
    }
  } catch {
    // Return original if formatting fails
  }
  return phone
}
